import { LogOut, User as UserIcon } from 'lucide-react';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { User } from '@/api/auth';
import { authApi, type LoginCredentials } from '@/api/auth';

interface NavBarItem {
  name: string;
  onClick?: () => void;
  href?: string;
  icon?: React.ReactNode;
}

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
  navBarItems: NavBarItem[];
  restrictFeatureByProfile: (code: string) => boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const userData = await authApi.me();
      setUser(userData);
    } catch {
      localStorage.removeItem('token');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = async (credentials: LoginCredentials) => {
    const response = await authApi.login(credentials);
    localStorage.setItem('token', response.token);
    setUser(response.user);
  };

  const logout = useCallback(() => {
    authApi.logout().catch(() => {
      // Ignore logout API errors
    });
    localStorage.removeItem('token');
    setUser(null);
  }, []);

  const navBarItems: NavBarItem[] = useMemo(
    () => [
      {
        name: 'Perfil',
        href: '/admin/user/profile',
        icon: <UserIcon className="h-5 w-5 shrink-0 text-gray-400" />,
      },
      {
        name: 'Sair',
        onClick: logout,
        icon: <LogOut className="h-5 w-5 shrink-0 text-gray-400" />,
      },
    ],
    [logout],
  );

  // Check if the user has permission for a specific code
  const restrictFeatureByProfile = useCallback(
    (code: string) => {
      if (!user?.permissions) return false;
      return user.permissions.includes(code);
    },
    [user?.permissions],
  );

  // Refresh the current user profile
  const refreshProfile = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  // Check authentication on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Inactivity timeout
  useEffect(() => {
    if (!user) return;

    let timeout: ReturnType<typeof setTimeout>;

    const resetTimeout = () => {
      clearTimeout(timeout);
      timeout = setTimeout(logout, INACTIVITY_TIMEOUT_MS);
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach((event) => {
      window.addEventListener(event, resetTimeout);
    });
    resetTimeout();

    return () => {
      clearTimeout(timeout);
      events.forEach((event) => {
        window.removeEventListener(event, resetTimeout);
      });
    };
  }, [user, logout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        logout,
        navBarItems,
        restrictFeatureByProfile,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
